import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
// Chemical equations in maths: \ce{2H2 + O2 -> 2H2O}. Registers the macro on
// the same katex instance rehype-katex uses.
import 'katex/contrib/mhchem';
import type { Mermaid } from 'mermaid';

import { InfoPopover } from '@/components/core/common/InfoPopover';
import {
  Enlargeable,
  useInsideEnlargeable,
} from '@/components/core/visual/Enlargeable';

import {
  FIGURE_SURFACE,
  MERMAID_THEME,
  VEGA_CONFIG,
  seriesColour,
} from './diagramTheme';
import {
  NumberLineFigure,
  numberLineLabel,
  type NumberLineSpec,
} from './fences/NumberLineBlock';
import { FenceFailed, FenceFallbackContext } from './fences/FenceFailed';
import { VennFigure, vennLabel, type VennSpec } from './fences/VennBlock';
import { repairMermaid } from './mermaidRepair';
import { mermaidMaths } from '@/lib/markdown/unicodeMath';
import { useFontsReady } from '@/hooks/ui/useFontsReady';

// Shared markdown rendering config for every learning-content surface.
//
// Enables, on top of plain markdown:
//   - GitHub tables/strikethrough (remark-gfm)
//   - LaTeX math via KaTeX (remark-math -> rehype-katex)
//   - code syntax highlighting (rehype-highlight)
//   - ```mermaid fenced diagrams (custom code/pre components below)
//
// Components keep their own `a` renderer (e.g. citation chips) by passing it
// through `createMarkdownComponents(overrides)`.

export const REMARK_PLUGINS: Options['remarkPlugins'] = [remarkGfm, remarkMath];

export const REHYPE_PLUGINS: Options['rehypePlugins'] = [
  rehypeHighlight,
  // Malformed LaTeX must never crash the page - render the error inline.
  [rehypeKatex, { throwOnError: false, errorColor: '#dc2626' }],
];

// Generated figures share one white surface and one palette (diagramTheme.ts).

// mermaid is heavy (~hundreds of KB), so it's dynamically imported the first
// time a diagram actually renders - keeping it out of the main bundle for the
// common case where a lesson has no diagram.
let mermaidPromise: Promise<Mermaid> | null = null;

function ensureMermaid(): Promise<Mermaid> {
  if (!mermaidPromise) {
    mermaidPromise = import('mermaid').then(({ default: mermaid }) => {
      mermaid.initialize({
        startOnLoad: false,
        // The Xonera figure theme (light surface in both app themes).
        ...MERMAID_THEME,
        // Mermaid defaults to its own '"trebuchet ms", verdana, arial' stack
        // and writes it into the generated SVG, so diagram labels ignore the
        // inherited page font unless it is set here.
        fontFamily: '"Inter Variable", Inter, system-ui, sans-serif',
        // LLM-authored diagram source - sanitise aggressively.
        securityLevel: 'strict',
        // Without this, mermaid 11 does NOT reject on a syntax error: render()
        // resolves with its "Syntax error in text" bomb SVG, which we would
        // inject. Rejecting lets MermaidBlock fall back to the source instead.
        suppressErrorRendering: true,
      });
      return mermaid;
    });
  }
  return mermaidPromise;
}

let mermaidSeq = 0;

/**
 * A fresh id per render PASS - never one per component, and never minted in
 * render. mermaid writes the id into the SVG's own stylesheet and into
 * `#d<id>`, the temp element it measures in and then deletes BY ID,
 * document-wide. Two passes sharing one id - which StrictMode's
 * mount -> unmount -> mount guarantees - each delete the other's workspace
 * mid-measure, and the loser throws into the failure path.
 */
const nextMermaidId = () => `mermaid-${(mermaidSeq += 1)}`;

/** The face mermaid measures labels in; MERMAID_THEME draws them at 14px. */
const LABEL_FONTS = ['14px "Inter Variable"'] as const;

/**
 * The source to render, its labels' maths as Unicode (`lib/markdown/unicodeMath`):
 * as written when it parses, otherwise the quoted-label
 * repair (LLM diagrams often put `(` inside `[...]`). If the repair doesn't
 * parse either, render() rejects and MermaidBlock shows the raw source.
 */
async function renderableSource(mermaid: Mermaid, written: string) {
  // Maths in a label as Unicode text: Mermaid draws `$x^2$` literally.
  const chart = mermaidMaths(written);
  const ok = await mermaid.parse(chart, { suppressErrors: true });
  return ok ? chart : repairMermaid(chart);
}

/** A sideways flowchart drawn below this share of its size is unreadable
 *  (labels under ~9px): it is drawn top-to-bottom instead. */
const MIN_FLOW_SCALE = 0.7;
const SIDEWAYS_FLOW = /^(\s*(?:graph|flowchart)\s+)(LR|RL)\b/;

/** The chart drawn top-to-bottom, or null when it isn't a sideways flowchart. */
export function uprightFlow(chart: string): string | null {
  return SIDEWAYS_FLOW.test(chart)
    ? chart.replace(SIDEWAYS_FLOW, '$1TD')
    : null;
}

/** How much the drawn SVG is shrunk to fit its box (1 = full size). */
function drawnScale(box: HTMLElement): number {
  const svg = box.querySelector('svg');
  const width = svg?.viewBox.baseVal?.width;
  // A hidden box (a closed tab) has no width yet: nothing to judge.
  return svg && width && box.clientWidth
    ? Math.min(1, box.clientWidth / width)
    : 1;
}

/** A diagram source that stays unparseable this long has failed (a streamed
 *  one changes much sooner). */
const FAIL_AFTER_MS = 500;

/** Whether this markdown is a compact card body (`BlockMarkdown`), where
 *  `CARD_VISUAL_CAP` height-caps every figure. */
const InCardContext = createContext(false);

/**
 * A drawn figure, plus - in a card - a button that opens it full size.
 *
 * `CARD_VISUAL_CAP` caps a figure at 16rem, and the browser then scales the
 * whole drawing down to fit: a seven-node flowchart measured 0.38, about 5px
 * of label text, which is not readable. The card keeps the preview, but stops
 * being the only way to read the figure. The dialog portals to the body, so
 * the cap does not reach the enlarged copy.
 *
 * Button only, never a tap on the figure: on a flashcard or a quiz a click on
 * the card already flips or answers it.
 */
function CardEnlargeable({
  label,
  large,
  children,
}: {
  label: string;
  large: ReactNode;
  children: ReactNode;
}) {
  const inCard = useContext(InCardContext);
  // A concept diagram is wrapped by VisualBlock's own Enlargeable already.
  const enlargeable = useInsideEnlargeable();
  if (!inCard || enlargeable) return <>{children}</>;
  return (
    <Enlargeable label={label} large={large} buttonOnly>
      {children}
    </Enlargeable>
  );
}

/** The enlarged copy must not think it is in a card, or it caps itself. */
function Uncapped({ children }: { children: ReactNode }) {
  return (
    <InCardContext.Provider value={false}>{children}</InCardContext.Provider>
  );
}

/** Render a ```mermaid fenced block into an SVG diagram (async, error-safe). */
export function MermaidBlock({ chart }: { chart: string }) {
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);
  // A sideways flowchart too wide for its box is redrawn top-to-bottom.
  const [upright, setUpright] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  // Measured in the fallback face, every node is sized for text mermaid will
  // not draw, and the label overflows its own box once Inter swaps in.
  const fontsReady = useFontsReady(LABEL_FONTS);
  const source = (upright && uprightFlow(chart)) || chart;

  useEffect(() => {
    const box = boxRef.current;
    if (!svg || upright || !box || !uprightFlow(chart)) return;
    if (drawnScale(box) < MIN_FLOW_SCALE) setUpright(true);
  }, [svg, upright, chart]);

  useEffect(() => {
    if (!fontsReady) return;
    let cancelled = false;
    let failLater: number | undefined;
    const id = nextMermaidId();
    ensureMermaid()
      .then(async mermaid =>
        mermaid.render(id, await renderableSource(mermaid, source))
      )
      .then(({ svg }) => {
        if (cancelled) return;
        setSvg(svg);
        setFailed(false);
      })
      .catch(() => {
        // A chat answer streams its fence in: the half-written source fails
        // on every chunk. Only a source that stays broken is a failure.
        if (!cancelled)
          failLater = window.setTimeout(() => setFailed(true), FAIL_AFTER_MS);
      });
    return () => {
      cancelled = true;
      window.clearTimeout(failLater);
    };
  }, [source, fontsReady]);

  if (failed) return <FenceFailed kind='mermaid' />;
  if (!svg) {
    return (
      <div className='py-2 text-sm text-muted-foreground'>
        Rendering diagram…
      </div>
    );
  }
  const figure = (
    <div
      ref={boxRef}
      className={FIGURE_SURFACE}
      role='img'
      aria-label='diagram'
      // eslint-disable-next-line react/no-danger -- svg is mermaid output, sanitised via securityLevel:'strict'
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
  return (
    <CardEnlargeable
      label='diagram'
      large={
        <div
          className={FIGURE_SURFACE}
          role='img'
          aria-label='diagram'
          // eslint-disable-next-line react/no-danger -- same mermaid output as the preview
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      }
    >
      {figure}
    </CardEnlargeable>
  );
}

// ---------------------------------------------------------------------------
// Client-rendered DSL blocks (charts / function plots / molecules).
//
// Same instant, deterministic pattern as MermaidBlock: the LLM emits a small
// text spec in a fenced block, a lazily-loaded library draws it in-browser
// (<100ms, no server render). Any parse/render error falls back to the raw
// source so a bad spec is still legible and never crashes the page.
// ---------------------------------------------------------------------------

/** Lazily render a DSL spec into a container; error-safe with raw fallback. */
function useDslRender(
  // A draw may return a disposer for anything it leaves running (the function
  // plot's resize observer). Only the hook knows when `inner` is detached.
  draw: (el: HTMLDivElement) => Promise<void | (() => void)>,
  source: string
) {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const host = ref.current;
    if (!host) return;
    let cancelled = false;
    let failLater: number | undefined;
    let dispose: (() => void) | undefined;
    setFailed(false);
    // Draw into a FRESH inner node each run. On StrictMode's mount→unmount→
    // mount (or any re-render), cleanup detaches the old node, so a late async
    // draw (e.g. smiles-drawer's parse callback) lands in a detached element
    // and never double-paints. Without this the molecule rendered twice.
    host.replaceChildren();
    const inner = document.createElement('div');
    host.appendChild(inner);
    Promise.resolve()
      .then(() => draw(inner))
      .then(cleanup => {
        // A draw that lands AFTER cleanup (StrictMode's mount→unmount→mount)
        // is disposed on arrival rather than stored and leaked.
        if (cancelled) cleanup?.();
        else dispose = cleanup ?? undefined;
      })
      .catch(() => {
        // A streamed fence fails on every half-written chunk: only a source
        // that stays broken is a failure (see MermaidBlock).
        if (!cancelled) {
          failLater = window.setTimeout(() => setFailed(true), FAIL_AFTER_MS);
        }
      });
    return () => {
      cancelled = true;
      dispose?.();
      window.clearTimeout(failLater);
      inner.remove();
    };
    // `draw` is recreated each render; `source` is the real dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  return { ref, failed };
}

function DslBlock({
  source,
  draw,
  label,
  kind,
}: {
  source: string;
  draw: (el: HTMLDivElement) => Promise<void | (() => void)>;
  label: string;
  /** The fence kind, for the fallback's wording and the failure report. */
  kind: CustomFence;
}) {
  const { ref, failed } = useDslRender(draw, source);
  // The drawing host stays mounted while the failure shows, so a source that
  // changes (the rest of a streamed answer) still has somewhere to draw.
  const figure = (
    <>
      {failed && <FenceFailed kind={kind} />}
      <div
        ref={ref}
        className={FIGURE_SURFACE}
        role='img'
        aria-label={label}
        data-dsl-figure
        hidden={failed}
      />
    </>
  );
  if (failed) return figure;
  return (
    <CardEnlargeable
      label={label}
      // Drawn a second time, into the dialog's own host - and only once it
      // opens, since a closed Modal renders nothing.
      large={
        <Uncapped>
          <DslBlock source={source} draw={draw} label={label} kind={kind} />
        </Uncapped>
      }
    >
      {figure}
    </CardEnlargeable>
  );
}

// Vega-Lite charts (```chart) - LLM emits a Vega-Lite JSON spec.
let vegaEmbedPromise: Promise<typeof import('vega-embed').default> | null =
  null;
function ensureVegaEmbed() {
  if (!vegaEmbedPromise) {
    vegaEmbedPromise = import('vega-embed').then(m => m.default);
  }
  return vegaEmbedPromise;
}

export function ChartBlock({ spec }: { spec: string }) {
  return (
    <DslBlock
      label='chart'
      kind='chart'
      source={spec}
      draw={async el => {
        const embed = await ensureVegaEmbed();
        // Render at the spec's natural size (Vega defaults are small and fit a
        // card); the figure surface caps width + scrolls if a chart is wide.
        await embed(el, JSON.parse(spec), {
          actions: false,
          renderer: 'svg',
          config: VEGA_CONFIG,
        });
      }}
    />
  );
}

// 2-D function graphs (```funcplot) - one expression per line ("y = x^2 - 3x"),
// or a full function-plot options object as JSON.
let functionPlotPromise: Promise<
  typeof import('function-plot').default
> | null = null;
function ensureFunctionPlot() {
  if (!functionPlotPromise) {
    functionPlotPromise = import('function-plot').then(m => m.default);
  }
  return functionPlotPromise;
}

/**
 * The function-plot options a ```funcplot block describes: a full options
 * object as JSON, or one expression per line ("y = x^2 - 3x"). Trailing
 * `# ...` / `// ...` comments - which models like to add - are stripped, since
 * function-plot would reject the whole graph over them. Exported so the
 * content eval checks plots exactly the way this block draws them.
 */
export function functionPlotOptions(source: string): {
  data?: { fn?: string }[];
  /** Set only by a spec that writes a full options object; such a spec owns
   *  its own size and opts out of the container sizing below. */
  width?: number;
  height?: number;
} {
  const trimmed = source.trim();
  if (trimmed.startsWith('{')) return JSON.parse(trimmed);
  return {
    data: trimmed
      .split('\n')
      .map(line => line.replace(/\s*(#|\/\/).*$/, '').trim())
      .filter(Boolean)
      .map(fn => ({ fn: fn.replace(/^y\s*=\s*/i, '') })),
  };
}

// A plot is drawn to the width its container gives it, with its HEIGHT tied to
// that width. A fixed 200px height against a box up to 520px wide drew a 2.6:1
// letterbox, and function-plot keeps the axes square: on a 460x160 canvas over
// x in [-6,6] it derives y in [-2.1, 2.1], so y = x^2 left the frame at x ~ 1.4.
// Tying the two together keeps px-per-unit equal on both axes at every size, so
// the window it derives is the same whatever the container does.
const PLOT_MIN_WIDTH = 220;
const PLOT_MAX_WIDTH = 520;
const PLOT_ASPECT = 1.6;
const PLOT_MIN_HEIGHT = 150;
const PLOT_MAX_HEIGHT = 340;
/** What a container with no width yet (a closed tab, a card mid-open) is drawn
 *  at until the observer reports a real one. */
const PLOT_UNMEASURED_WIDTH = 360;
/** A width change under this is not worth a redraw - and a scrollbar that
 *  appears and disappears must never start one redrawing the other. */
const PLOT_RESIZE_STEP = 8;

/** The pixel box a plot gets inside a container this wide (0 = not laid out). */
export function plotBox(containerWidth: number): {
  width: number;
  height: number;
} {
  const width = Math.max(
    PLOT_MIN_WIDTH,
    Math.min(containerWidth || PLOT_UNMEASURED_WIDTH, PLOT_MAX_WIDTH)
  );
  const height = Math.round(
    Math.min(Math.max(width / PLOT_ASPECT, PLOT_MIN_HEIGHT), PLOT_MAX_HEIGHT)
  );
  return { width, height };
}

/**
 * Draw a ```funcplot spec into `el` and keep it sized to `el`. Returns the
 * disposer `useDslRender` calls on unmount / source change.
 */
async function drawFunctionPlot(
  el: HTMLDivElement,
  source: string
): Promise<() => void> {
  const functionPlot = await ensureFunctionPlot();
  const spec = functionPlotOptions(source);
  // Series colours from the figure theme unless the spec sets its own.
  spec.data = (spec.data ?? []).map((d, i) => ({
    color: seriesColour(i),
    ...d,
  }));

  const box = plotBox(el.clientWidth);
  // ONE options object for the life of this draw. function-plot caches the
  // chart on `options.id` and writes the drawn domains back into the object,
  // so calling it again with the SAME object redraws THAT chart in place and
  // keeps the window already on screen. A fresh object per resize would build
  // a new Chart and strand the old one in function-plot's module-level cache.
  const options = {
    target: el,
    width: box.width,
    height: box.height,
    grid: true,
    // A spec with its own width / height / xAxis / yAxis / grid wins: LAST.
    ...spec,
  } as unknown as Parameters<typeof functionPlot>[0] & {
    width: number;
    height: number;
  };
  functionPlot(options);

  const ownSize = spec.width !== undefined || spec.height !== undefined;
  if (ownSize || typeof ResizeObserver === 'undefined') return () => {};

  // `el` is a block child of the figure surface: its width is the surface's
  // content width, and the drawn SVG - which the surface scrolls rather than
  // stretches to - cannot change it. So observing it cannot feed back.
  let drawn = options.width;
  const observer = new ResizeObserver(() => {
    const next = plotBox(el.clientWidth);
    if (Math.abs(next.width - drawn) < PLOT_RESIZE_STEP) return;
    drawn = next.width;
    options.width = next.width;
    options.height = next.height;
    try {
      functionPlot(options);
    } catch {
      // Keep the plot already on screen: a resize must never blank it.
    }
  });
  observer.observe(el);
  return () => observer.disconnect();
}

export function FunctionPlotBlock({ source }: { source: string }) {
  return (
    <DslBlock
      label='function graph'
      kind='funcplot'
      source={source}
      draw={el => drawFunctionPlot(el, source)}
    />
  );
}

// Chemical structures (```smiles) - LLM emits a SMILES string; smiles-drawer
// (untyped) renders it to SVG. Minimal typed surface for the bits we call.
interface SmilesDrawerLib {
  parse(
    smiles: string,
    success: (tree: unknown) => void,
    error: (err: unknown) => void
  ): void;
  SvgDrawer: new (options?: Record<string, unknown>) => {
    draw(tree: unknown, target: SVGElement | string, theme: string): void;
  };
}
let smilesDrawerPromise: Promise<SmilesDrawerLib> | null = null;
function ensureSmilesDrawer() {
  if (!smilesDrawerPromise) {
    smilesDrawerPromise = import('smiles-drawer').then(
      m =>
        ((m as { default?: SmilesDrawerLib }).default ??
          m) as unknown as SmilesDrawerLib
    );
  }
  return smilesDrawerPromise;
}

export function SmilesBlock({ smiles }: { smiles: string }) {
  return (
    <DslBlock
      label='chemical structure'
      kind='smiles'
      source={smiles}
      draw={el =>
        new Promise<void>((resolve, reject) => {
          ensureSmilesDrawer()
            .then(lib => {
              const svg = document.createElementNS(
                'http://www.w3.org/2000/svg',
                'svg'
              );
              // Explicit box so the drawer emits sized SVG; the figure surface
              // scales it down responsively via max-width. Always 'light' theme
              // since it renders on a white figure surface in both modes.
              svg.setAttribute('width', '260');
              svg.setAttribute('height', '200');
              el.appendChild(svg);
              lib.parse(
                smiles.trim(),
                tree => {
                  try {
                    new lib.SvgDrawer({ width: 260, height: 200 }).draw(
                      tree,
                      svg,
                      'light'
                    );
                    resolve();
                  } catch (err) {
                    reject(err);
                  }
                },
                err => reject(err)
              );
            })
            .catch(reject);
        })
      }
    />
  );
}

// Music notation (```abc) - LLM emits ABC notation; abcjs renders it to SVG.
let abcjsPromise: Promise<typeof import('abcjs')> | null = null;
function ensureAbcjs() {
  if (!abcjsPromise) abcjsPromise = import('abcjs');
  return abcjsPromise;
}

export function MusicBlock({ abc }: { abc: string }) {
  return (
    <DslBlock
      label='music notation'
      kind='music'
      source={abc}
      draw={async el => {
        const { renderAbc } = await ensureAbcjs();
        renderAbc(el, abc, { responsive: 'resize' });
      }}
    />
  );
}

// Geometry figures (```geometry) - LLM emits a SAFE declarative JSON spec, NOT
// executable JS. We interpret a whitelist of coordinate-based primitives into
// JSXGraph board.create calls, so there's no eval of model-authored code.
interface JsxBoard {
  create(type: string, args: unknown, attrs?: Record<string, unknown>): unknown;
}
interface JsxGraphLib {
  JSXGraph: {
    initBoard(
      box: string | HTMLElement,
      attrs: Record<string, unknown>
    ): JsxBoard;
  };
}
let jsxgraphPromise: Promise<JsxGraphLib> | null = null;
let jsxgraphSeq = 0;

/**
 * The board parents a spec gives, as JSXGraph takes them. A polygon and an
 * angle take their points as a flat list - `[[0,0],[4,0],[0,3]]` - and reject
 * them wrapped in one more array (`[[[0,0],[4,0],[0,3]]]`, "Can't create point
 * with parent types"). The wrapped form is what the lesson prompt's own example
 * taught, so every polygon and right-angle marker was skipped, unseen.
 */
function boardArgs(type: string, args: unknown[]): unknown[] {
  const [only] = args;
  const isPoint = (p: unknown) =>
    Array.isArray(p) && p.length === 2 && p.every(v => typeof v === 'number');
  return (type === 'polygon' || type === 'angle') &&
    args.length === 1 &&
    Array.isArray(only) &&
    only.length >= 3 &&
    only.every(isPoint)
    ? only
    : args;
}

/**
 * JSXGraph letters every unnamed point A, B, C... in creation order. Those
 * letters mean nothing to the reader - the figure's own labels are its text
 * elements and named points - and they land on them: "B" over "focus", "E"
 * beside the point the text calls P. A point is labelled only by its name.
 */
function boardAttrs(type: string, attrs?: Record<string, unknown>) {
  return type === 'point' && !attrs?.name
    ? { withLabel: false, ...attrs }
    : (attrs ?? {});
}

function ensureJsxgraph() {
  if (!jsxgraphPromise) {
    jsxgraphPromise = import('jsxgraph').then(
      m =>
        ((m as { default?: JsxGraphLib }).default ??
          m) as unknown as JsxGraphLib
    );
  }
  return jsxgraphPromise;
}

// Coordinate-based primitives only (no element-reference or function-string
// types) - keeps the spec fully declarative and safe. Exported for the content
// eval's render check (backend/evals/funnel), which must skip what this skips.
export const GEOMETRY_TYPES = new Set([
  'point',
  'line',
  'segment',
  'circle',
  'ellipse',
  'arc',
  'polygon',
  'angle',
  'vector',
  'text',
]);

interface GeometrySpec {
  boundingbox?: number[];
  axis?: boolean;
  keepAspectRatio?: boolean;
  elements?: Array<{
    type: string;
    args: unknown;
    attrs?: Record<string, unknown>;
  }>;
}

export function GeometryBlock({ spec }: { spec: string }) {
  return (
    <DslBlock
      label='geometry figure'
      kind='geometry'
      source={spec}
      draw={async el => {
        const JXG = await ensureJsxgraph();
        const parsed = JSON.parse(spec) as GeometrySpec;
        const box = document.createElement('div');
        // Size to the container width (capped) with a square-ish height so
        // circles stay circular; fits compact cards without overflow.
        const width = Math.max(220, Math.min(el.clientWidth || 320, 420));
        box.style.width = `${width}px`;
        box.style.height = '260px';
        // JSXGraph names a board's clip path and filters `<container id>_...`.
        // Without an id every board's is `_ClipFull`, the same id, and a
        // `url(#_ClipFull)` reference resolves to the FIRST in the document:
        // every figure on the page was clipped to the size of whichever board
        // came first (a 268px card clipped a 420px lesson figure to a square).
        box.id = `jxg-board-${(jsxgraphSeq += 1)}`;
        el.appendChild(box);
        const board = JXG.JSXGraph.initBoard(box, {
          boundingbox: parsed.boundingbox ?? [-5, 5, 5, -5],
          axis: parsed.axis ?? true,
          keepAspectRatio: parsed.keepAspectRatio ?? true,
          showCopyright: false,
          showNavigation: false,
        });
        let drawn = 0;
        for (const item of parsed.elements ?? []) {
          // No `args`, nothing to place it by. Most types throw on that, but a
          // polygon is created silently empty and would count as drawn.
          if (!GEOMETRY_TYPES.has(item.type) || !Array.isArray(item.args)) {
            continue;
          }
          try {
            board.create(
              item.type,
              boardArgs(item.type, item.args),
              boardAttrs(item.type, item.attrs)
            );
            drawn += 1;
          } catch {
            // skip a malformed element; keep the rest of the board
          }
        }
        // Every element skipped leaves an empty white box with nothing to say
        // it failed - fail instead, so the figure is reported (or hidden in a
        // card) like any other that cannot be drawn.
        if (!drawn) throw new Error('no element of the figure could be drawn');
      }}
    />
  );
}

// Map a fenced code block's language class to a custom renderer (or null for a
// normal code block). Aliases keep the LLM's fence tags forgiving.
export type CustomFence =
  | 'mermaid'
  | 'chart'
  | 'funcplot'
  | 'smiles'
  | 'music'
  | 'geometry'
  | 'venn'
  | 'numberline';

// Fence tag (and forgiving aliases) -> renderer. Exported for the content
// eval's render check, so it classifies fences exactly as the app does.
export const FENCE_ALIASES: Record<string, CustomFence> = {
  mermaid: 'mermaid',
  chart: 'chart',
  'vega-lite': 'chart',
  vegalite: 'chart',
  funcplot: 'funcplot',
  plot: 'funcplot',
  smiles: 'smiles',
  chem: 'smiles',
  abc: 'music',
  music: 'music',
  geometry: 'geometry',
  jsxgraph: 'geometry',
  geo: 'geometry',
  venn: 'venn',
  numberline: 'numberline',
  'number-line': 'numberline',
};

/** A JSON-spec figure drawn as plain SVG (venn, number line); a spec that
 *  does not parse falls back like every other fence (`FenceFailed`). */
function SvgSpecBlock<T>({
  source,
  label,
  render,
  kind,
}: {
  kind: CustomFence;
  source: string;
  label: (spec: T) => string;
  render: (spec: T) => React.ReactNode;
}) {
  let spec: T;
  try {
    spec = JSON.parse(source) as T;
  } catch {
    return <FenceFailed kind={kind} />;
  }
  const figure = (
    <div className={FIGURE_SURFACE} role='img' aria-label={label(spec)}>
      {render(spec)}
    </div>
  );
  return (
    <CardEnlargeable label={label(spec)} large={figure}>
      {figure}
    </CardEnlargeable>
  );
}

function customFence(className: unknown): CustomFence | null {
  if (typeof className !== 'string') return null;
  const match = className.match(/\blanguage-([\w-]+)\b/);
  return match ? (FENCE_ALIASES[match[1]] ?? null) : null;
}

function renderFence(kind: CustomFence, source: string) {
  switch (kind) {
    case 'mermaid':
      return <MermaidBlock chart={source} />;
    case 'chart':
      return <ChartBlock spec={source} />;
    case 'funcplot':
      return <FunctionPlotBlock source={source} />;
    case 'smiles':
      return <SmilesBlock smiles={source} />;
    case 'music':
      return <MusicBlock abc={source} />;
    case 'geometry':
      return <GeometryBlock spec={source} />;
    case 'venn':
      return (
        <SvgSpecBlock<VennSpec>
          kind='venn'
          source={source}
          label={vennLabel}
          render={spec => <VennFigure spec={spec} />}
        />
      );
    case 'numberline':
      return (
        <SvgSpecBlock<NumberLineSpec>
          kind='numberline'
          source={source}
          label={numberLineLabel}
          render={spec => <NumberLineFigure spec={spec} />}
        />
      );
  }
}

// Module-level so the map keeps one identity for the app's lifetime.
// ReactMarkdown uses these functions as component TYPES: a map rebuilt per
// render hands it new types every time, and React unmounts and re-mounts
// every overridden element — every fenced plot/chart/diagram redrawn on each
// parent re-render (the practice runner's 1s timer tick collapsed its plot
// and reset the page's scroll once a second). Same for MARKDOWN_COMPONENTS,
// built once at the bottom of this module.
const INLINE_COMPONENTS: Components = {
  p: ({ children }) => <>{children}</>,
  img: () => null,
};

/**
 * Inline markdown for truly tiny slots (quiz option chips): renders inline code
 * + LaTeX math but unwraps the block `<p>` so it flows inside the surrounding
 * element without extra layout. For card BODY fields use BlockMarkdown instead.
 */
export function InlineMarkdown({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  return (
    <span className={className}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={INLINE_COMPONENTS}
      >
        {children}
      </ReactMarkdown>
    </span>
  );
}

// Cap visuals so a figure never blows out a compact card: center + bound SVG/
// image height, make wide tables/code/display-math scroll instead of overflow.
const CARD_VISUAL_CAP =
  '[&_svg]:mx-auto [&_svg]:max-h-64 [&_svg]:max-w-full ' +
  '[&_img]:mx-auto [&_img]:max-h-64 ' +
  '[&_table]:block [&_table]:w-fit [&_table]:overflow-x-auto ' +
  '[&_pre]:overflow-x-auto [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden';

/**
 * A wide equation, table or code block scrolls sideways inside a card. A
 * region that scrolls must be reachable from the keyboard (WCAG 2.1.1), so
 * each one that actually overflows gets a tab stop and a name.
 */
function useFocusableOverflow(content: string) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const mark = () => {
      for (const el of root.querySelectorAll<HTMLElement>(
        '.katex-display, table, pre'
      )) {
        const scrolls =
          el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
        if (!scrolls || el.tabIndex === 0) continue;
        el.tabIndex = 0;
        if (!el.getAttribute('aria-label') && !el.closest('[role="img"]')) {
          el.setAttribute('role', 'region');
          el.setAttribute(
            'aria-label',
            el.matches('table')
              ? 'Table'
              : el.matches('pre')
                ? 'Code'
                : 'Equation'
          );
        }
      }
    };
    mark();
    // Widths settle once the maths fonts load and when the card resizes.
    void document.fonts?.ready.then(mark);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(mark);
    ro.observe(root);
    return () => ro.disconnect();
  }, [content]);
  return ref;
}

/**
 * Block markdown for card BODY fields (quiz question stem + explanation,
 * flashcard front/back, highlight body, glossary definition). Renders the FULL
 * pipeline - tables, mermaid, chart, funcplot, smiles, geometry, music - with
 * visuals size-capped so they fit a compact card. Use InlineMarkdown only for
 * tiny slots like quiz option chips.
 */
export function BlockMarkdown({
  children,
  className,
}: {
  children: string;
  className?: string;
}) {
  const ref = useFocusableOverflow(children);
  return (
    <div
      ref={ref}
      className={[CARD_VISUAL_CAP, className].filter(Boolean).join(' ')}
    >
      {/* In a card a visual that fails leaves no trace (FenceFailed). */}
      <InCardContext.Provider value={true}>
        <FenceFallbackContext.Provider value='hide'>
          <ReactMarkdown
            remarkPlugins={REMARK_PLUGINS}
            rehypePlugins={REHYPE_PLUGINS}
            components={CARD_COMPONENTS}
          >
            {children}
          </ReactMarkdown>
        </FenceFallbackContext.Provider>
      </InCardContext.Provider>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Curated library images (```![alt](img://<id>)``` references).
//
// LLM-authored lesson content references a curated image ONLY by id via the
// `img://` scheme (mirrors the `cite://` citation pattern). The frontend
// resolves the id to a vetted library URL; any OTHER img src from model output
// is rejected, so the model can never inject an arbitrary/hallucinated origin.
// ---------------------------------------------------------------------------

/** Resolved metadata for a curated library image (from the lesson response). */
export interface ResolvedLibraryImage {
  url: string;
  alt?: string;
  caption?: string;
  attribution?: string;
  license?: string;
  source_url?: string;
  width?: number;
  height?: number;
}

/** Credit details behind the figure's info icon. */
function LibraryImageCredit({ image }: { image: ResolvedLibraryImage }) {
  return (
    <div className='space-y-2'>
      {image.caption && <p>{image.caption}</p>}
      {image.attribution && (
        <p>
          <span className='font-medium'>Credit: </span>
          {image.attribution}
        </p>
      )}
      {image.source_url && (
        <a
          href={image.source_url}
          target='_blank'
          rel='noopener noreferrer'
          className='inline-block font-medium text-primary underline-offset-2 hover:underline'
        >
          View original
        </a>
      )}
    </div>
  );
}

/** The visible caption, and whether anything is left to show behind the icon. */
function figureCaption(image: ResolvedLibraryImage, title?: string) {
  const heading = title?.trim() || image.caption || '';
  const hiddenCaption = heading !== image.caption && Boolean(image.caption);
  const hasDetails =
    hiddenCaption || Boolean(image.attribution || image.source_url);
  return { heading, hasDetails };
}

/**
 * Render a resolved library image on the figure surface. The visible caption
 * is `title` - the short, lesson-specific name the model wrote as the image's
 * markdown alt text - falling back to the library caption. The long caption
 * and the licence credit sit behind an info icon: still one hover / tap away
 * (the credit is required, never dropped), but no longer competing with the
 * lesson.
 */
export function LibraryImageFigure({
  image,
  title,
}: {
  image: ResolvedLibraryImage;
  title?: string;
}) {
  const { heading, hasDetails } = figureCaption(image, title);
  const alt = image.alt || image.caption || heading;
  const picture = (large: boolean) => (
    <img
      src={image.url}
      alt={alt}
      loading='lazy'
      className={
        large
          ? 'mx-auto max-h-[75dvh] w-auto max-w-full rounded-sm'
          : 'mx-auto max-h-72 w-auto max-w-full rounded-sm'
      }
    />
  );
  return (
    <Enlargeable
      label={heading || alt || 'Picture'}
      large={
        <figure>
          {picture(true)}
          {heading && (
            <figcaption className='mt-2 text-center text-sm text-muted-foreground'>
              {heading}
            </figcaption>
          )}
        </figure>
      }
    >
      <figure className={FIGURE_SURFACE}>
        {picture(false)}
        {(heading || hasDetails) && (
          <figcaption className='mt-2 flex items-center justify-center gap-1 text-xs text-muted-foreground'>
            {heading && <span>{heading}</span>}
            {hasDetails && (
              <InfoPopover label='Image details and credit'>
                <LibraryImageCredit image={image} />
              </InfoPopover>
            )}
          </figcaption>
        )}
      </figure>
    </Enlargeable>
  );
}

/**
 * Build an `img` renderer for LLM surfaces: resolves `img://<id>` to a curated
 * library image and REJECTS any other src (no external/arbitrary URLs from
 * model output). Pass into `createMarkdownComponents({ img: … })`.
 */
export function libraryImageRenderer(
  resolve: (id: string) => ResolvedLibraryImage | undefined
) {
  return function Img({ src, alt }: { src?: string; alt?: string }) {
    if (typeof src === 'string' && src.startsWith('img://')) {
      const image = resolve(src.slice('img://'.length));
      return image ? <LibraryImageFigure image={image} title={alt} /> : null;
    }
    // Reject any non-library image (arbitrary/hallucinated URL) - render nothing.
    return null;
  };
}

/**
 * Build the shared `components` map for ReactMarkdown. `overrides` lets a
 * caller inject its own renderers (e.g. a citation `a`) while keeping the
 * mermaid-aware `code`/`pre` handling.
 */
export function createMarkdownComponents(
  overrides: Partial<Components> = {}
): Components {
  return {
    // Route custom fences (mermaid/chart/funcplot/smiles) to their renderer;
    // everything else keeps the highlighted output rehype-highlight produced.
    code({ className, children, ...props }) {
      const kind = customFence(className);
      if (kind) {
        return renderFence(kind, String(children ?? '').replace(/\n$/, ''));
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    },
    // Unwrap the <pre> around a custom block so a <div> renderer isn't nested
    // inside <pre> (invalid HTML); normal code blocks keep their <pre>.
    pre({ children, ...props }) {
      const child = Array.isArray(children) ? children[0] : children;
      const cls = (child as { props?: { className?: unknown } })?.props
        ?.className;
      if (customFence(cls)) return <>{children}</>;
      return <pre {...props}>{children}</pre>;
    },
    // A picture renders as a <figure> (a concept visual, a library figure),
    // which may not sit inside a <p>: its paragraph becomes a <div>.
    p({ node, children, ...props }) {
      const pictured = node?.children.some(
        c => c.type === 'element' && c.tagName === 'img'
      );
      return pictured ? (
        <div {...props}>{children}</div>
      ) : (
        <p {...props}>{children}</p>
      );
    },
    ...overrides,
  };
}

/**
 * The default `components` map, built ONCE. Pass this — never an inline
 * `createMarkdownComponents()` — to any ReactMarkdown with no per-caller
 * overrides: its identity must be stable across renders (see
 * INLINE_COMPONENTS). A caller that needs overrides builds them once with
 * `useMemo` and reads per-render data from a ref (MessageBubble,
 * TextRenderer).
 */
export const MARKDOWN_COMPONENTS: Components = createMarkdownComponents();

/**
 * Card fields (quiz, flashcards, highlights, glossary) are model-written: an
 * image there may only come from the artifact's `visual`, never from a URL in
 * the text, so markdown images are dropped. Built once, like the map above.
 */
const CARD_COMPONENTS: Components = createMarkdownComponents({
  img: () => null,
});
